import { useDisclosure, Separator } from '@chakra-ui/react';
import {
  AssociationEntity,
  IContractWithQuotes,
  IQuoteRespSchema,
  QuoteStatusEnum,
  QuoteTypeEnum,
  QuoteTypeEnumDisplay,
} from '@monetize/types/app';
import { toDateShort } from '@monetize/utils/core';
import { keepPreviousData } from '@tanstack/react-query';
import {
  AppColumnDef,
  createColumnHelper,
} from '../Monetize/DataTableNew/appTable';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Link,
  Location,
  Params,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  useChangeContractOwner,
  useGetContractById,
} from '../../api/cpqService';
import {
  CONTRACT_END_ACTION_UI_DISPLAY,
  CONTRACT_STATUS_DISPLAY,
} from '../../constants/contracts';
import {
  QuoteStatusEnumDisplay,
  QuoteStatusTagStyle,
} from '../../constants/quotes';
import {
  getAccountContractEditRoute,
  getAccountContractsRoute,
  getBillGroupPageRoute,
} from '../../constants/routes';
import {
  useContractLineage,
  useRenewalQuotesByLineage,
} from '../../hooks/useContractLineages';
import {
  getLineageContract,
  replaceContractIdInPathname,
} from '../../utils/contractLineage';
import { getQuoteLink, openQuote } from '../../utils/quotes';
import {
  MBox,
  MCenterModal,
  MGrid,
  MGridItem,
  MHStack,
  MLink,
  MPageHeader,
  MSkeleton,
  MTag,
  MText,
} from '../Monetize';
import { ExternalAssociationsIcons } from '../ExternalAssociations/ExternalAssociationsIcons';
import { ChangeOwner } from '../Monetize/ChangeOwner';
import { DataTable } from '../Monetize/DataTableNew/DataTable';
import {
  currencyBodyTemplate,
  dateTimeBodyTemplate,
  enumDisplayBodyTemplate,
  getColHeaderWithSize,
  nameWithIdBodyTemplate,
  userAvatarBodyTemplate,
} from '../Monetize/DataTableNew/tableUtilsNew';
import { MCurrency } from '../Monetize/MCurrency';
import { TitleValuePair } from '../Monetize/TitleValuePair';
import { RenewalModal } from '../RenewalModal/RenewalModal';
import { ContractActionButtons } from './ContractActionButtons';
import { ContractLineageRail } from './Lineage/ContractLineageRail';
import { ContractStatusTag } from './Lineage/ContractStatusTag';

interface ContractModalProps {
  isOpen: boolean;
  onClose: (
    params?: Readonly<Params<string>>,
    source?: 'accounts' | 'dashboard',
  ) => void;
  /**
   * Provide if modal is being opened without a specific URL route
   */
  contractId?: string;
  /**
   * Switch between lineage terms without navigating. Needed where the parent
   * route remounts on every navigation (the quote page), which would otherwise
   * close and reopen the modal.
   */
  isContractSwitchedInPlace?: boolean;
}
interface ContractModalLocation extends Location {
  state: { source?: 'accounts' | 'dashboard' };
}

const columnHelper = createColumnHelper<IQuoteRespSchema>();

const HEADER_TAGS_STYLE = { display: 'inline-flex', alignItems: 'center' };

// The API returns a contract's quotes in no particular order; newest first
const QUOTES_INITIAL_SORTING_STATE = [{ id: 'createDate', desc: true }];

const getColumns = (): AppColumnDef<IQuoteRespSchema, any>[] => [
  columnHelper.accessor('id', {
    ...getColHeaderWithSize('Name', 250),
    cell: nameWithIdBodyTemplate<IQuoteRespSchema, string>({
      nameProp: 'description',
      idProp: 'id',
      idLinkFn: (id, data) =>
        getQuoteLink({ quoteId: id, status: data.status }),
      openInNewTab: true,
    }),
  }),
  columnHelper.accessor('ownerName', {
    ...getColHeaderWithSize('Owner', { min: 50, max: 90, offset: 0 }),
    size: 90,
    cell: userAvatarBodyTemplate<IQuoteRespSchema, string>({
      extraProps: {
        mr: '21px',
      },
    }),
  }),
  columnHelper.accessor('type', {
    ...getColHeaderWithSize('Type', 150),
    cell: enumDisplayBodyTemplate<IQuoteRespSchema, QuoteTypeEnum>({
      displayMap: QuoteTypeEnumDisplay,
    }),
  }),
  columnHelper.accessor('status', {
    ...getColHeaderWithSize('Status', 120),
    cell: enumDisplayBodyTemplate<IQuoteRespSchema, QuoteStatusEnum>({
      displayMap: QuoteStatusEnumDisplay,
      styleMap: QuoteStatusTagStyle,
    }),
  }),
  columnHelper.accessor('amount', {
    ...getColHeaderWithSize('Total', 150),
    cell: currencyBodyTemplate<IQuoteRespSchema, number>({
      currencyProperty: 'currency',
    }),
  }),
  columnHelper.accessor('modifyDate', {
    ...getColHeaderWithSize('Updated', 170),
    cell: dateTimeBodyTemplate(),
  }),
  columnHelper.accessor('createDate', {
    ...getColHeaderWithSize('Created', 170),
    cell: dateTimeBodyTemplate(),
  }),
];

const ContractModal = React.forwardRef(
  (
    {
      contractId: _contractId,
      isOpen,
      onClose,
      isContractSwitchedInPlace = false,
    }: ContractModalProps,
    ref,
  ) => {
    const {
      open: isOpenRenewalModal,
      onOpen: onOpenRenewalModal,
      onClose: onCloseRenewalModal,
    } = useDisclosure();

    const navigate = useNavigate();
    const params = useParams();
    const [contractId, setContractId] = useState(
      _contractId || params?.contractId || '',
    );
    const location = useLocation() as ContractModalLocation;
    const { state } = location;

    useEffect(() => {
      if (!_contractId && params?.contractId) {
        setContractId(params.contractId);
      }
    }, [params.contractId, _contractId]);

    const {
      data: fetchedContract,
      isLoading: isContractLoading,
      isPlaceholderData: isSwitchingContract,
      isError,
    } = useGetContractById(contractId, {
      enabled: isOpen && !!contractId,
      meta: { showErrorToast: true },
      // Keeps the account known while the next term loads (see `contract` below)
      placeholderData: keepPreviousData,
    });

    const { lineage, isLoading: isLineageLoading } = useContractLineage({
      contractId,
      accountId: fetchedContract?.accountId,
    });
    const viewedTerm = lineage
      ? getLineageContract(lineage, contractId)
      : undefined;
    const billGroup = viewedTerm?.billGroup;

    const lineages = useMemo(() => (lineage ? [lineage] : []), [lineage]);
    const renewalQuotesByLineageId = useRenewalQuotesByLineage({
      accountId: fetchedContract?.accountId,
      lineages,
    });
    const renewalQuotes = lineage
      ? renewalQuotesByLineageId.get(lineage.id)
      : undefined;

    // While the next term loads, show it from the lineage's list data so nothing
    // on screen (or actionable) still belongs to the previous term. Only the
    // quotes, which the list does not include, wait for the fetch. A contract
    // outside the cached lineage renders nothing until its own fetch lands.
    let contract: IContractWithQuotes | undefined = fetchedContract;
    if (isSwitchingContract) {
      contract = viewedTerm && { ...viewedTerm, quotes: [] };
    }

    const handleSelectContract = useCallback(
      (nextContractId: string) => {
        if (nextContractId === contractId) {
          return;
        }
        // Opened without a route (e.g. from a lookup popover): swap in place
        if (_contractId || !params.contractId || isContractSwitchedInPlace) {
          setContractId(nextContractId);
          return;
        }
        navigate(
          {
            pathname: replaceContractIdInPathname(
              location.pathname,
              params.contractId,
              nextContractId,
            ),
            search: location.search,
          },
          { replace: true, state },
        );
      },
      [
        contractId,
        _contractId,
        params.contractId,
        isContractSwitchedInPlace,
        navigate,
        location.pathname,
        location.search,
        state,
      ],
    );

    useEffect(() => {
      if (isError) {
        onClose(params, state?.source);
      }
    }, [isError, onClose, params, state?.source]);

    const { mutate: changeContractOwner } = useChangeContractOwner();

    const handleChangeOwner = (e: React.ChangeEvent<HTMLInputElement>) => {
      changeContractOwner({ contractId, userId: String(e) });
    };

    const columns = useMemo(() => getColumns(), []);

    const isQuotesLoading = isContractLoading || isSwitchingContract;

    const handleEarlyRenewal = () => {
      onOpenRenewalModal();
    };

    // Points at the term being viewed, even when it was switched in place. A
    // modal opened without a route (lookup popover) links to the account's copy.
    const isRouteDriven = !_contractId && !!params.contractId;
    let copyUrl: string | boolean = true;
    if (isRouteDriven) {
      copyUrl = `${window.location.origin}${replaceContractIdInPathname(
        location.pathname,
        params.contractId ?? '',
        contractId,
      )}${location.search}`;
    } else if (contract) {
      copyUrl = `${window.location.origin}${getAccountContractEditRoute(
        contract.accountId,
        contractId,
      )}`;
    }

    const title = contract && (
      <MBox flex="1" minW={0}>
        <MPageHeader
          title="Contract Details"
          tag={
            <>
              <ContractStatusTag status={contract.status} />
              {contract.renewed && (
                <MTag variant="purple" ml={2}>
                  Renewed
                </MTag>
              )}
            </>
          }
          tagStyle={HEADER_TAGS_STYLE}
          parentLink={getAccountContractsRoute(contract.accountId)}
          parentLinkTitle={contract.accountName}
          copyUrl={copyUrl}
          id={contract?.id}
          recordAuditInfo={contract}
          entityJson={contract}
          extraBadges={[
            <ExternalAssociationsIcons
              key="ExternalAssociationsIcons"
              entity={AssociationEntity.CONTRACT}
              recordId={contract.id}
            />,
          ]}
          subTitle={
            <MBox>
              <MHStack gap={3}>
                <MText as="span" fontSize="sm" color="fg.muted">
                  Owner
                </MText>
                <ChangeOwner
                  value={contract.owner}
                  ownerName={contract.ownerName}
                  onChange={handleChangeOwner}
                />
              </MHStack>
            </MBox>
          }
          containerProps={{ mb: 0 }}
        >
          <ContractActionButtons
            // Remount per term: the cancel form's defaults come from the contract
            key={contract.id}
            contract={contract}
            handleEarlyRenewal={handleEarlyRenewal}
          />
        </MPageHeader>
      </MBox>
    );

    const contractOverviewValues = [
      {
        title: 'Contract Period',
        value: `${
          contract?.startDate ? toDateShort(contract.startDate) : ''
        } - ${contract?.endDate ? toDateShort(contract.endDate) : ''}`,
        width: 2,
      },
      {
        title: 'Status',
        value: contract?.status ? CONTRACT_STATUS_DISPLAY[contract.status] : '',
        width: 1,
      },
      {
        title: 'Renewal Action',
        value: contract?.endAction
          ? CONTRACT_END_ACTION_UI_DISPLAY[contract.endAction]
          : '',
        width: 2,
      },
      {
        title: 'Currency',
        value: contract?.currency ? contract.currency : '',
        width: 1,
      },
      {
        title: 'Bill Group',
        value: billGroup ? (
          <MLink asChild fontWeight="bold" lineClamp={1}>
            <Link to={getBillGroupPageRoute(billGroup.id)}>
              {billGroup.name}
            </Link>
          </MLink>
        ) : (
          contract?.billGroupId
        ),
        width: 2,
      },
      {
        title: 'Contract Value',
        value: contract?.currency ? (
          <MCurrency
            amount={contract?.totalValue}
            fontWeight="bold"
            fontSize="inherit"
            options={{
              currency: contract.currency,
            }}
          />
        ) : null,
        width: 2,
      },
    ];

    if (contract && isOpenRenewalModal) {
      return <RenewalModal contract={contract} onClose={onCloseRenewalModal} />;
    }

    return (
      <MCenterModal
        isOpen={isOpen}
        onClose={() => onClose(params, state?.source)}
        size="full"
        modalContentProps={{ 'data-testid': 'contract-form' } as any}
        // Room for the close button beside the action buttons
        modalHeaderProps={{ pr: 12 }}
        renderModalTitleActions={() => title}
      >
        <MGrid
          templateColumns={{ base: '1fr', lg: '300px minmax(0, 1fr)' }}
          gap={6}
          alignItems="start"
        >
          <MBox position={{ lg: 'sticky' }} top={0}>
            <ContractLineageRail
              lineage={lineage}
              isLoading={isContractLoading || isLineageLoading}
              viewedContractId={contractId}
              onSelect={handleSelectContract}
              renewalQuotes={renewalQuotes}
            />
          </MBox>
          <MBox minW={0}>
            {isContractLoading || !contract ? (
              <MSkeleton height={10} w="full" />
            ) : (
              <MGrid
                templateColumns="repeat(10, 1fr)"
                columnGap={4}
                rowGap={4}
                fontSize="md"
              >
                {contractOverviewValues.map((item, index) => {
                  return (
                    <MGridItem key={index} colSpan={item.width}>
                      <TitleValuePair title={item.title} value={item.value} />
                    </MGridItem>
                  );
                })}
              </MGrid>
            )}
            <Separator
              mt={6}
              mb={5}
              borderWidth="2px"
              borderColor="tBlue.hover"
            />
            <MText fontSize="md" fontWeight="bold">
              Quotes
            </MText>
            {isQuotesLoading && <MSkeleton height={100} width="full" mt={8} />}
            {!isQuotesLoading && (
              <DataTable
                columns={columns}
                data={contract?.quotes || []}
                isLoading={isQuotesLoading}
                enablePagination={false}
                clientControlled
                initialSortingState={QUOTES_INITIAL_SORTING_STATE}
                onRowClick={(row) => openQuote(navigate, row.id, row.status)}
                emptyProps={{
                  mainMessage:
                    'Looks like there is a problem displaying the quotes here.',
                }}
              />
            )}
          </MBox>
        </MGrid>
      </MCenterModal>
    );
  },
);

export default ContractModal;
